import { el } from "./dom.js";

/**
 * P1-6 (DCTA-192, polish audit): what a line must never break inside — a date with its time or year ("13 Oct 09:00",
 * "30 Sep 2027"), a count with its word ("1 held", "2 of 3 done", "3 steps") — each kept whole in a `.nw` span
 * (vault: white-space nowrap). The audit saw "13 / Oct 09:00" and "1 / held". Render-only: the words, their spaces and
 * what a screen reader hears are exactly the text given, and the text is only ever text (never parsed as markup).
 */
const MONTH = "(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)";
const TOGETHER = new RegExp(
  [
    `\\b\\d{1,2} ${MONTH}(?: \\d{4})?(?: \\d{1,2}:\\d{2})?\\b`,
    `\\b\\d+ of \\d+(?: done)?\\b`,
    `\\b\\d+ (?:held|not sent|done|steps?|operations?|payments?|holdings?)\\b`,
  ].join("|"),
  "g",
);

/** `text` with each match of `re` (global; dates and counts by default) kept whole in a `.nw` span, the rest as plain strings. */
export function keepTogether(text: string, re: RegExp = TOGETHER): (string | HTMLElement)[] {
  const out: (string | HTMLElement)[] = [];
  let at = 0;
  for (const m of text.matchAll(re)) {
    if (m.index > at) out.push(text.slice(at, m.index));
    out.push(el("span", { class: "nw" }, [m[0]]));
    at = m.index + m[0].length;
  }
  if (at < text.length || out.length === 0) out.push(text.slice(at));
  return out;
}

/** The same, through parts another renderer made (an inert form set apart as data stays as it is). */
export function keepParts(parts: readonly (string | Node)[]): (string | Node)[] {
  return parts.flatMap((p): (string | Node)[] => (typeof p === "string" ? keepTogether(p) : [p]));
}

/** "→ after" as one piece (P1-6): the arrow drawn, "to" read (16-pay.html's delta pattern), the figure (`cls`) never split from it. */
export function arrowTo(after: string, cls = "after"): HTMLElement {
  return el("span", { class: "nw" }, [el("span", { class: "arrow", aria: { hidden: "true" } }, ["→"]), el("span", { class: "vis-hidden" }, ["to"]), " ", el("span", { class: cls }, [after])]);
}
